/**
 * Maximum length of the raw URL string accepted by the UI. Mirrors the backend constant
 * `UrlPolicy.UrlMaxLength`; keep these two in sync if either changes.
 */
export const MAX_URL_LENGTH = 2048;

/**
 * UX-only validator for HTTPS URL inputs.
 *
 * Intent: provide fast inline feedback so users don't submit obviously-wrong values and wait
 * for a server round-trip. The backend's `UrlPolicy` is authoritative — this helper is
 * deliberately permissive about cases the server can disambiguate better (IDN hosts,
 * trailing dots, etc.) and only catches the cheap-and-obvious failures.
 *
 * Rules:
 * - `null`/`undefined`/empty-string → valid (URL is optional, blank means "clear").
 * - Trimmed length must not exceed {@link MAX_URL_LENGTH}.
 * - Must start (case-insensitively) with `https://`.
 * - Must parse as a URL with scheme `https:` and no `userinfo`.
 *
 * @param value - Raw user input.
 * @returns `true` when the value is plausibly valid; `false` only when we're confident the
 *   server will reject it.
 */
export const isLikelyValidHttpsUrl = (
  value: string | null | undefined,
): boolean => {
  if (value == null) {
    return true;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return true;
  }

  if (trimmed.length > MAX_URL_LENGTH) {
    return false;
  }

  if (!trimmed.toLowerCase().startsWith('https://')) {
    return false;
  }

  try {
    const parsed = new URL(trimmed);

    const lastDotIndex = parsed.hostname.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return false;
    }
    const tld = parsed.hostname.substring(lastDotIndex + 1);
    if (!/^[a-z]{2,}$/i.test(tld)) {
      return false;
    }

    return (
      parsed.protocol === 'https:' &&
      parsed.username.length === 0 &&
      parsed.password.length === 0
    );
  } catch {
    return false;
  }
};

/**
 * Normalizes raw user input into the canonical wire shape: trimmed string, or `null` when empty.
 *
 * @param value - Raw user input.
 * @returns The trimmed string, or `null` if the input was nullish/whitespace-only.
 */
export const normalizeOptionalUrlInput = (
  value: string | null | undefined,
): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
};
